import { saveAs } from 'file-saver'
import JSZip from 'jszip'

export type DownloadSingleFileParams = {
  url: string
  downloadableName: string
  folder?: string
}

export interface DownloadZipFileParams {
  zipFileName: string
  files: DownloadSingleFileParams[]
}

/**
 * Sanitizes a filename for Windows compatibility.
 * Windows' built-in compressed folder handler has issues with special characters
 * like #, [, ], and &. 7-Zip handles these fine, but most clients use default Windows.
 */
const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[#\[\]&]/g, '')
}

export const downloadZip = async (params: DownloadZipFileParams) => {
  const { zipFileName, files } = params
  const zip = new JSZip()

  // Fetch files and add them to the zip
  const fetchPromises = files.map(async (file, index: number) => {
    const response = await fetch(file.url)
    const blob = await response.blob()
    // Sanitize filename for Windows compatibility
    const sanitizedName = sanitizeFilename(file.downloadableName)
    const fileName = `${index + 1}. ${sanitizedName}`

    // If folder is specified, create the path with folder/filename
    const filePath = file.folder ? `${sanitizeFilename(file.folder)}/${fileName}` : fileName
    zip.file(filePath, blob)
  })

  await Promise.all(fetchPromises)

  // Ensure .zip extension and sanitize filename for Windows
  const safeZipFileName = sanitizeFilename(zipFileName).replace(/\.zip$/i, '') + '.zip'

  // Generate the zip file
  const content = await zip.generateAsync({ type: 'blob' })

  // Save the zip file
  saveAs(content, safeZipFileName)
}

export const downloadFile = async (params: DownloadSingleFileParams) => {
  const response = await fetch(params.url)
  const blob = await response.blob()
  saveAs(blob, params.downloadableName)
}
